import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart, FileText } from 'lucide-react';

export default function Terms() {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                                <span className="font-bold text-lg text-white">Autoimmune Pathways</span>
                                <span className="text-xs sm:text-sm text-slate-400">by Me and My Pandas</span>
                            </div>
                        </Link>
                        <Link to="/">
                            <Button variant="ghost" className="text-slate-300 hover:text-white gap-2">
                                <ArrowLeft className="w-4 h-4" />
                                Back to Home
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-3 mb-8">
                        <FileText className="w-10 h-10 text-indigo-400" />
                        <h1 className="text-3xl sm:text-4xl font-bold text-white">Terms of Service</h1>
                    </div>
                    
                    <div className="prose prose-invert prose-slate max-w-none">
                        <p className="text-slate-400 text-lg mb-8">
                            Last Updated: May 25, 2026
                        </p>

                        <div className="space-y-8 text-slate-300">
                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
                                <p>
                                    Welcome to Autoimmune Pathways ("Autoimmune Pathways," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the Autoimmune Pathways mobile application, website, services, features, content, and related technologies (collectively, the "Service").
                                </p>
                                <p className="mt-4">
                                    By accessing or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use the Service.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">2. Eligibility</h2>
                                <p>You must be at least 18 years old to use the Service.</p>
                                <p className="mt-4">By using the Service, you represent and warrant that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>You are at least 18 years old;</li>
                                    <li>You have the legal capacity to enter into these Terms;</li>
                                    <li>All information you provide is accurate and truthful.</li>
                                </ul>
                                <p className="mt-4">
                                    The Service is intended for adults managing or tracking autoimmune-related symptoms, wellness information, and personal health data.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">3. Description of the Service</h2>
                                <p>
                                    Autoimmune Pathways is a health and wellness tracking platform designed to help users organize, monitor, and better understand personal health-related information.
                                </p>
                                <p className="mt-4">Features may include:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Symptom tracking</li>
                                    <li>Medication and supplement tracking</li>
                                    <li>Food and nutrition logging</li>
                                    <li>Sleep and wellness tracking</li>
                                    <li>Data visualization and correlation tools</li>
                                    <li>AI-assisted educational tools</li>
                                    <li>Community features</li>
                                    <li>Educational resources</li>
                                    <li>Reporting and export functionality</li>
                                </ul>
                                <p className="mt-4">
                                    The Service is intended solely for informational, organizational, wellness, and educational purposes.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">4. IMPORTANT MEDICAL DISCLAIMER</h2>
                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                                    <p className="text-amber-200 font-medium">
                                        AUTOIMMUNE PATHWAYS IS NOT A MEDICAL PROVIDER AND DOES NOT PROVIDE MEDICAL CARE, DIAGNOSIS, OR TREATMENT.
                                    </p>
                                </div>
                                <p>
                                    The Service, including all content, tracking tools, AI-generated responses, correlations, educational materials, reports, notifications, and recommendations, is provided solely for informational and educational purposes.
                                </p>
                                <p className="mt-4">Nothing within the Service should be interpreted as:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Medical advice;</li>
                                    <li>Mental health advice;</li>
                                    <li>Diagnosis;</li>
                                    <li>Treatment recommendations;</li>
                                    <li>Medication instructions;</li>
                                    <li>Emergency guidance;</li>
                                    <li>Clinical decision-making support.</li>
                                </ul>
                                <p className="mt-4">
                                    Always seek the advice of a licensed physician or qualified healthcare provider regarding any medical condition or treatment decisions.
                                </p>
                                <p className="mt-4">
                                    Never disregard professional medical advice or delay seeking medical treatment because of information obtained through the Service.
                                </p>
                                <p className="mt-4">
                                    If you believe you may be experiencing a medical emergency, immediately contact emergency services or your physician.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">5. No Provider-Patient Relationship</h2>
                                <p>Your use of the Service does not create:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>A physician-patient relationship;</li>
                                    <li>Therapist-patient relationship;</li>
                                    <li>Provider-patient relationship;</li>
                                    <li>Fiduciary relationship;</li>
                                    <li>Clinical treatment relationship of any kind.</li>
                                </ul>
                                <p className="mt-4">
                                    No communication through the Service, including AI-generated responses, community interactions, or educational content, establishes a professional healthcare relationship.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">6. AI-Generated Content Disclaimer</h2>
                                <p>
                                    The Service may utilize artificial intelligence ("AI") technologies to provide educational insights, organizational assistance, summaries, or informational responses.
                                </p>
                                <p className="mt-4">You acknowledge and agree that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>AI-generated content may be inaccurate, incomplete, outdated, inappropriate, or misleading;</li>
                                    <li>AI outputs are generated automatically and are not reviewed by licensed medical professionals;</li>
                                    <li>AI-generated information should never be relied upon for diagnosis, treatment, medication adjustments, or emergency situations;</li>
                                    <li>You are solely responsible for evaluating and verifying any information generated by the Service.</li>
                                </ul>
                                <p className="mt-4">
                                    AI features are provided "AS IS" without warranties of any kind.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">7. Correlation and Insights Disclaimer</h2>
                                <p>
                                    The Service may generate trends, correlations, summaries, visualizations, or insights based on information you provide.
                                </p>
                                <p className="mt-4">You acknowledge that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Correlation does not establish causation;</li>
                                    <li>Patterns identified by the Service do not constitute medical conclusions;</li>
                                    <li>Generated insights are not diagnostic tools;</li>
                                    <li>The Service is not intended to predict, prevent, or treat medical conditions.</li>
                                </ul>
                                <p className="mt-4">
                                    Any health-related decisions should be made in consultation with qualified healthcare professionals.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">8. Emergency and Crisis Disclaimer</h2>
                                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                                    <p className="text-red-200 font-medium">
                                        THE SERVICE IS NOT MONITORED FOR EMERGENCIES.
                                    </p>
                                </div>
                                <p>Autoimmune Pathways does not provide:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Crisis intervention;</li>
                                    <li>Suicide prevention services;</li>
                                    <li>Emergency monitoring;</li>
                                    <li>Real-time medical supervision.</li>
                                </ul>
                                <p className="mt-4">
                                    Messages, symptom logs, AI interactions, or community posts may not be reviewed immediately or at all.
                                </p>
                                <p className="mt-4">
                                    You should never rely on the Service during medical, psychiatric, or mental health emergencies.
                                </p>
                                <p className="mt-4">If you are experiencing an emergency, contact:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>911 or your local emergency services;</li>
                                    <li>Your physician;</li>
                                    <li>A licensed healthcare provider;</li>
                                    <li>Emergency medical personnel.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">9. User Accounts</h2>
                                <p>To access certain features, you may be required to create an account.</p>
                                <p className="mt-4">You agree to:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Provide accurate and current information;</li>
                                    <li>Maintain the confidentiality of your login credentials;</li>
                                    <li>Accept responsibility for all activity under your account;</li>
                                    <li>Notify us promptly of unauthorized access or security breaches.</li>
                                </ul>
                                <p className="mt-4">We reserve the right to suspend or terminate accounts for:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Violations of these Terms;</li>
                                    <li>Unsafe or harmful conduct;</li>
                                    <li>Fraudulent activity;</li>
                                    <li>Abuse of community or AI features;</li>
                                    <li>Misuse of the Service.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">10. Privacy and Data Use</h2>
                                <p>
                                    Your use of the Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference.
                                </p>
                                <p className="mt-4">By using the Service, you acknowledge and agree that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>You voluntarily provide health-related information;</li>
                                    <li>We may store and process information necessary to operate and improve the Service;</li>
                                    <li>Data transmission over the internet can never be guaranteed completely secure.</li>
                                </ul>
                                <p className="mt-4">
                                    We take commercially reasonable measures to protect user information; however, we cannot guarantee absolute security.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">11. HIPAA and Health Information</h2>
                                <p>
                                    Unless explicitly stated otherwise in a separate written agreement, Autoimmune Pathways is not acting as a healthcare provider or covered entity under HIPAA.
                                </p>
                                <p className="mt-4">
                                    The Service is intended as a consumer wellness and organizational platform.
                                </p>
                                <p className="mt-4">You acknowledge that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>The Service may not be suitable for storing highly sensitive medical records;</li>
                                    <li>You use the Service at your own discretion and risk.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">12. Subscription and Billing</h2>
                                <p>Certain features may require a paid subscription.</p>
                                <p className="mt-4">
                                    By subscribing, you authorize recurring charges to your selected payment method.
                                </p>
                                <p className="mt-4">You acknowledge and agree that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Subscription fees may renew automatically unless canceled;</li>
                                    <li>Pricing may change with notice;</li>
                                    <li>Failure to cancel prior to renewal may result in continued billing.</li>
                                </ul>
                                <p className="mt-4">
                                    Refunds, if any, are provided solely at our discretion unless otherwise required by law.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">13. User Content</h2>
                                <p>You retain ownership of content you submit to the Service, including:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Health data;</li>
                                    <li>Notes;</li>
                                    <li>Photos;</li>
                                    <li>Voice recordings;</li>
                                    <li>Community posts.</li>
                                </ul>
                                <p className="mt-4">
                                    By submitting content, you grant Autoimmune Pathways a non-exclusive, worldwide, royalty-free license to:
                                </p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Host;</li>
                                    <li>Store;</li>
                                    <li>Process;</li>
                                    <li>Display;</li>
                                    <li>Analyze;</li>
                                    <li>Improve the Service using such content.</li>
                                </ul>
                                <p className="mt-4">
                                    You represent and warrant that you have the legal right to submit all content you provide.
                                </p>
                                <p className="mt-4">You agree not to submit content that:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Violates laws or regulations;</li>
                                    <li>Infringes intellectual property rights;</li>
                                    <li>Contains malicious software;</li>
                                    <li>Harasses or harms others;</li>
                                    <li>Contains false or misleading information.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">14. Community Standards</h2>
                                <p>Users participating in community features agree to:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Interact respectfully;</li>
                                    <li>Avoid harassment or bullying;</li>
                                    <li>Avoid providing dangerous medical advice;</li>
                                    <li>Respect user privacy and confidentiality;</li>
                                    <li>Avoid spam or commercial solicitation.</li>
                                </ul>
                                <p className="mt-4">
                                    We reserve the right to remove content or restrict access at our discretion.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">15. Third-Party Services</h2>
                                <p>The Service may utilize third-party providers, including:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Cloud hosting providers;</li>
                                    <li>Payment processors;</li>
                                    <li>AI service providers;</li>
                                    <li>Analytics services;</li>
                                    <li>App store platforms.</li>
                                </ul>
                                <p className="mt-4">
                                    We are not responsible for the practices, availability, or actions of third-party services.
                                </p>
                                <p className="mt-4">
                                    Your use of third-party services may also be governed by their respective terms and policies.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">16. Intellectual Property</h2>
                                <p>
                                    The Service and all associated intellectual property are owned by Autoimmune Pathways and protected by applicable intellectual property laws.
                                </p>
                                <p className="mt-4">You may not:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Copy;</li>
                                    <li>Reproduce;</li>
                                    <li>Modify;</li>
                                    <li>Reverse engineer;</li>
                                    <li>Sell;</li>
                                    <li>Redistribute;</li>
                                    <li>Exploit the Service or its content without prior written consent.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">17. Disclaimer of Warranties</h2>
                                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4">
                                    <p className="text-slate-200 font-medium">
                                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE."
                                    </p>
                                </div>
                                <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>MERCHANTABILITY;</li>
                                    <li>FITNESS FOR A PARTICULAR PURPOSE;</li>
                                    <li>NON-INFRINGEMENT;</li>
                                    <li>ACCURACY;</li>
                                    <li>RELIABILITY;</li>
                                    <li>AVAILABILITY.</li>
                                </ul>
                                <p className="mt-4">We do not guarantee:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Continuous availability;</li>
                                    <li>Error-free operation;</li>
                                    <li>Preservation of data;</li>
                                    <li>Accuracy of generated content;</li>
                                    <li>Uninterrupted access.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">18. Limitation of Liability</h2>
                                <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, AUTOIMMUNE PATHWAYS SHALL NOT BE LIABLE FOR:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Indirect damages;</li>
                                    <li>Consequential damages;</li>
                                    <li>Incidental damages;</li>
                                    <li>Special damages;</li>
                                    <li>Loss of profits;</li>
                                    <li>Loss of data;</li>
                                    <li>Health-related outcomes;</li>
                                    <li>Reliance on generated insights or AI content.</li>
                                </ul>
                                <p className="mt-4 font-semibold text-white">
                                    YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.
                                </p>
                                <p className="mt-4">
                                    IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT PAID BY YOU TO THE SERVICE DURING THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">19. Indemnification</h2>
                                <p>
                                    You agree to defend, indemnify, and hold harmless Autoimmune Pathways and its affiliates, officers, employees, contractors, licensors, and agents from claims, damages, liabilities, and expenses arising from:
                                </p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Your use of the Service;</li>
                                    <li>Your violation of these Terms;</li>
                                    <li>Your submitted content;</li>
                                    <li>Your misuse of AI-generated content or health insights.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">20. Arbitration and Class Action Waiver</h2>
                                <p>
                                    Any dispute arising from these Terms or the Service shall be resolved through binding arbitration in the State of South Dakota.
                                </p>
                                <p className="mt-4">You agree:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>To waive the right to a jury trial;</li>
                                    <li>To waive participation in class actions or class arbitration.</li>
                                </ul>
                                <p className="mt-4">Claims must be brought individually.</p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">21. Termination</h2>
                                <p>
                                    We may suspend or terminate your access to the Service at any time, with or without notice, for any reason, including violations of these Terms.
                                </p>
                                <p className="mt-4">You may discontinue use of the Service at any time.</p>
                                <p className="mt-4">Upon termination:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>Your right to access the Service immediately ceases;</li>
                                    <li>Certain data may be retained as required by law or operational necessity.</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">22. Changes to the Terms</h2>
                                <p>We reserve the right to modify these Terms at any time.</p>
                                <p className="mt-4">Material changes may be communicated through:</p>
                                <ul className="list-disc pl-6 mt-2 space-y-2">
                                    <li>App notifications;</li>
                                    <li>Email;</li>
                                    <li>Website notices.</li>
                                </ul>
                                <p className="mt-4">
                                    Continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">23. Governing Law</h2>
                                <p>
                                    These Terms shall be governed by the laws of the State of South Dakota, without regard to conflict of law principles.
                                </p>
                                <p className="mt-4">
                                    Any legal proceedings not subject to arbitration shall occur exclusively in South Dakota.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-xl font-semibold text-white mb-4">24. Contact Information</h2>
                                <p>If you have questions regarding these Terms, please contact:</p>
                                <p className="mt-4">
                                    <strong>Me and My PANDAS LLC</strong><br />
                                    Email: <a href="mailto:contact@meandmypandas.com" className="text-indigo-400 hover:underline">contact@meandmypandas.com</a>
                                </p>
                            </section>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-950 border-t border-slate-800">
                <div className="max-w-7xl mx-auto text-center">
                    <p className="text-sm text-slate-500">
                        © {new Date().getFullYear()} Autoimmune Pathways. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
}
